import { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarCheck, Compass, Users, Clock, Grape, Send } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { LineReveal, Reveal } from "@/components/Motion";
import { Marquee } from "@/components/Marquee";
import { GalleryCarousel } from "@/components/GalleryCarousel";
import { Seo } from "@/components/Seo";
import { useContent } from "@/lib/api";

const steps = [
  { icon: Compass, title: "Explore the space", text: "Browse photos from real events held here — the hall, the corners, the details.", to: "/space", cta: "View the gallery" },
  { icon: CalendarCheck, title: "Check availability", text: "See open days and hours at a glance. Availability is indicative — we confirm every date personally.", to: "/availability", cta: "See the calendar" },
  { icon: Send, title: "Send an inquiry", text: "Tell us about your event in a few minutes. We reply personally to every inquiry.", to: "/inquire", cta: "Start an inquiry" },
];

export default function Home() {
  const { data } = useContent();
  const s = data?.settings || {};
  const c = data?.content || {};
  const gallery = c.gallery || [];
  const charcuterieGallery = c.charcuterie_gallery || [];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const [openStep, setOpenStep] = useState(null);

  return (
    <div className="bg-bone">
      <Seo title="The Owl's Nest — Event Venue & Charcuterie"
        description="An intimate event rental space with charcuterie offerings for up to 100 guests. Browse real event photos, check availability, and send an event inquiry." />
      <Nav />

      <section ref={heroRef} className="mx-auto max-w-7xl px-4 pt-32 sm:px-8 sm:pt-40 lg:px-16">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
          className="overline-label" data-testid="hero-overline">
          Private Event Venue · Charcuterie
        </motion.p>
        <LineReveal
          className="mt-5 font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          lines={[
            <>Where gatherings</>,
            <>become <span className="italic text-stone-500">heirlooms.</span></>,
          ]}
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg sm:leading-relaxed sm:font-light">
          An intimate event space for up to {s.capacity || 100} — birthdays, baby showers, wedding showers,
          employee lunches, and every reason in between. Tables and chairs included; the rest is yours to dream up.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/availability" data-testid="hero-reserve-venue-btn" className="btn-dark">
            Reserve the venue <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/inquire?type=charcuterie" data-testid="hero-order-charcuterie-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-gold">
            Order charcuterie <Grape className="h-4 w-4 text-gold transition-transform duration-300 group-hover:rotate-12" />
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }}
          className="relative mt-14 overflow-hidden rounded-3xl md:rounded-[2.5rem]">
          <motion.img
            src={gallery[0]?.url} alt={gallery[0]?.alt || "The Owl's Nest event hall set for a celebration"}
            style={{ y: imgY, scale: 1.18 }}
            className="h-[55vh] w-full object-cover sm:h-[68vh]" loading="eager" />
          <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-4 bg-gradient-to-t from-black/55 to-transparent p-6 sm:p-10">
            <p className="font-serif text-xl italic text-bone sm:text-2xl">{gallery[0]?.caption}</p>
            <div className="flex gap-2">
              <span className="rounded-full bg-bone/15 px-4 py-2 text-xs font-semibold text-bone backdrop-blur-md">
                <Users className="mr-1.5 inline h-3.5 w-3.5" />Up to {s.capacity || 100} guests
              </span>
              {s.show_pricing && s.hourly_rate && (
                <span className="rounded-full bg-bone/15 px-4 py-2 text-xs font-semibold text-bone backdrop-blur-md">
                  <Clock className="mr-1.5 inline h-3.5 w-3.5" />${s.hourly_rate}/hr · {s.min_duration_hours}-hr min
                </span>
              )}
            </div>
          </div>
        </motion.div>
      </section>

      <div className="mt-20 sm:mt-28"><Marquee /></div>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8 sm:py-20 lg:px-16 lg:py-24">
        <Reveal>
          <p className="overline-label">How it works</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            From first look to final toast, in three steps.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-0" data-testid="home-steps-ribbon">
            {steps.map((step, i) => (
              <Fragment key={step.title}>
                {i > 0 && <span className="hidden h-px w-8 shrink-0 bg-gold/50 sm:block lg:w-12" />}
                <button type="button" onClick={() => setOpenStep(openStep === i ? null : i)}
                  data-testid={`home-step-pill-${i}`} aria-expanded={openStep === i}
                  className={`flex items-center gap-3 rounded-full px-5 py-3 text-left ring-1 transition-colors duration-300 ${
                    openStep === i ? "bg-ink text-bone ring-ink" : "bg-white text-ink ring-black/10 hover:ring-gold/60"}`}>
                  <span className="font-serif text-lg italic text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-semibold">{step.title}</span>
                </button>
              </Fragment>
            ))}
          </div>
          <AnimatePresence initial={false}>
            {openStep !== null && (
              <motion.div key={openStep}
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }} className="overflow-hidden">
                <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white p-6 ring-1 ring-black/5"
                  data-testid="home-step-detail">
                  <p className="max-w-xl text-sm leading-relaxed text-stone-600">{steps[openStep].text}</p>
                  <Link to={steps[openStep].to} data-testid="home-step-detail-cta" className="btn-ghost !px-5 !py-2.5 text-xs">
                    {steps[openStep].cta} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </section>

      <section className="bg-bone2 py-16 sm:py-24 lg:py-28" id="venue" data-testid="home-venue-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="overline-label">The venue — for your event</p>
              <h2 className="mt-4 max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                Warm brick, timber beams, room for everyone you love.
              </h2>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-stone-600">
                One characterful room, one hourly rate. Tables and chairs come with the space — decor,
                catering, and vendors are yours to bring.
              </p>
            </div>
            <Link to="/space" data-testid="home-gallery-link" className="btn-ghost !px-5 !py-2.5 text-xs">
              Full gallery <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="home-basics">
            {[
              s.show_pricing && s.hourly_rate && { k: `$${s.hourly_rate}`, v: `per hour · ${s.min_duration_hours}-hour minimum` },
              { k: `${s.capacity || 100}`, v: "guests, seated or standing" },
              { k: "Included", v: "the space, tables & chairs" },
              { k: "Yours to style", v: "decor, vendors & your own vision" },
            ].filter(Boolean).map((item) => (
              <div key={item.k} className="rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:p-5">
                <p className="font-serif text-xl text-ink sm:text-2xl">{item.k}</p>
                <p className="mt-1 text-[11px] leading-snug text-stone-500 sm:text-xs">{item.v}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="mt-6">
            <Link to="/inquire" data-testid="home-basics-inquire-btn" className="btn-dark !px-6 !py-3 text-xs">
              Tell us about your event <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-10">
            <GalleryCarousel items={gallery} />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-28" data-testid="home-charcuterie-section" id="charcuterie">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="overline-label">Charcuterie — we come to you</p>
            <div className="mt-4 grid gap-8 lg:grid-cols-2 lg:items-end">
              <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                Hosting somewhere else? We bring the spread to you.
              </h2>
              <p className="max-w-xl text-base leading-relaxed text-stone-600">
                You don't need to rent our room to have our charcuterie. We deliver, style, and set up
                abundant boards and full grazing tables at your home, workplace, or any event space —
                ready before your first guest arrives. Cured meats, cheeses, fruit, honey, and all the
                little things people hover over.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="charcuterie-basics">
              {[
                { k: "Boards", v: "for intimate gatherings" },
                { k: "Grazing tables", v: "for the whole party" },
                { k: "We come to you", v: "delivery, styling & setup included" },
                { k: "Quoted per event", v: "sized to your guest count" },
              ].map((item) => (
                <div key={item.k} className="rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:p-5">
                  <p className="font-serif text-xl text-ink sm:text-2xl">{item.k}</p>
                  <p className="mt-1 text-[11px] leading-snug text-stone-500 sm:text-xs">{item.v}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/inquire?type=charcuterie" data-testid="home-charcuterie-only-btn" className="btn-dark !px-6 !py-3 text-xs">
                Book charcuterie for your event <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/inquire" className="btn-ghost !px-6 !py-3 text-xs">
                Add it to a venue inquiry <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          {charcuterieGallery.length > 0 && (
            <div className="relative mt-14">
              <span data-testid="charcuterie-badge"
                className="absolute -top-3 left-1/2 z-40 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink shadow-lg sm:left-6 sm:translate-x-0">
                We deliver &amp; set it up
              </span>
              <GalleryCarousel items={charcuterieGallery} idPrefix="charcuterie-gallery" />
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <Reveal className="card relative overflow-hidden bg-ink !p-0">
          <div className="relative z-10 px-8 py-16 text-center sm:px-16 sm:py-24">
            <p className="overline-label !text-gold">Begin the conversation</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl leading-tight tracking-tight text-bone sm:text-4xl lg:text-5xl">
              Tell us about your celebration.
            </h2>
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-stone-400">
              A few details is all it takes. We read every inquiry personally and reply within a day or two.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/inquire" data-testid="home-cta-inquire-btn"
                className="btn-dark !bg-bone !text-ink hover:!bg-white">
                Start an Inquiry <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/availability" className="btn-ghost !border-bone/25 !text-bone hover:!border-bone/60">
                Check dates first
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
